/**
 * Responsibility: add a watch, by pasting a product URL or by naming the
 * retailer and its item number directly.
 *
 * Pasting is the path the form leads with, because every retailer puts its own
 * identifier in the URL and a pasted address cannot disagree with itself. A
 * hand-typed identifier can point at nothing and poll a 404 forever while
 * reporting itself perfectly healthy, which is the quietest way this system
 * fails.
 *
 * The retailer is still a field rather than a read-only result. It fills in
 * from the URL and can be overridden, which is what makes the form usable when
 * a retailer changes its URL shape or when the operator has an item number and
 * no link.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCreateWatch } from '../../api/hooks/useWatches';
import { useNotifyLists } from '../../api/hooks/useNotify';
import { hubApi } from '../../api/client';
import type { UrlPreview } from '../../api/client';
import { NotifyListPicker } from '../ui/NotifyListPicker';

type AddWatchDialogProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Label, what that retailer calls its identifier, and where to find it when
 * the product has no page yet.
 *
 * That last part is the common case for a launch. A product you want to catch
 * at 3am on street date does not have a live page today, so a form that only
 * accepts a pasted URL would be useless for exactly the drop it was built for.
 */
const RETAILERS = [
  {
    value: 'TARGET',
    label: 'Target',
    idLabel: 'TCIN',
    hint: 'Often live on a preorder page before the product itself is.',
  },
  {
    value: 'WALMART',
    label: 'Walmart',
    idLabel: 'item ID',
    hint: 'The long number at the end of a /ip/ link.',
  },
  {
    value: 'BESTBUY',
    label: 'Best Buy',
    idLabel: 'SKU',
    hint: 'On the product page and in the URL before the .p',
  },
  {
    value: 'COSTCO',
    label: 'Costco',
    idLabel: 'item number',
    hint: 'The page 404s until launch - that flip is the signal we watch for.',
  },
  {
    value: 'AMAZON',
    label: 'Amazon',
    idLabel: 'ASIN',
    hint: 'Ten characters, in the URL after /dp/',
  },
] as const;

const PLACEHOLDER = 'https://www.target.com/p/pokemon-.../-/A-1010892076';

export function AddWatchDialog({ open, onClose }: AddWatchDialogProps) {
  const [url, setUrl] = useState('');
  const [retailer, setRetailer] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [notifyLists, setNotifyLists] = useState<string[]>([]);
  const [groupKey, setGroupKey] = useState('');
  const [msrp, setMsrp] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  const [preview, setPreview] = useState<UrlPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const create = useCreateWatch();
  const { data: knownLists } = useNotifyLists();

  useEffect(() => {
    if (open) return;
    setUrl('');
    setRetailer('');
    setIdentifier('');
    setName('');
    setNotifyLists([]);
    setGroupKey('');
    setMsrp('');
    setMaxPrice('');
    setPreview(null);
    setPreviewError(null);
    create.reset();
    // Resetting on every mutation state change would clear the form mid-submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const trimmed = url.trim();
    if (!trimmed) {
      setPreview(null);
      setPreviewError(null);
      return;
    }

    // Debounced: a paste should not fire a request per keystroke.
    let cancelled = false;
    const timer = window.setTimeout(() => {
      hubApi
        .previewWatchUrl(trimmed)
        .then((result) => {
          if (cancelled) return;
          setPreview(result);
          setPreviewError(null);
          // Fill the fields in rather than hiding them, so what was detected
          // is visible and correctable.
          setRetailer(result.retailer);
          setIdentifier(result.identifier);
          // Only when the operator has not written their own name: a paste
          // must never overwrite something they typed.
          setName((current) => (current.trim() === '' ? (result.suggested_name ?? '') : current));
        })
        .catch((error: Error) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewError(error.message);
        });
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [url]);

  const selected = RETAILERS.find((entry) => entry.value === retailer);
  const canSubmit =
    retailer !== '' &&
    identifier.trim() !== '' &&
    name.trim() !== '' &&
    !preview?.already_tracked &&
    !create.isPending;

  const submit = () => {
    create.mutate(
      {
        // Send the parts rather than the URL: the operator may have corrected
        // them, and the server must honour what is on screen.
        retailer: retailer as 'TARGET',
        identifier: identifier.trim(),
        product_name: name.trim(),
        notify_lists: notifyLists,
        group_key: groupKey.trim() || null,
        msrp: msrp.trim() || null,
        absolute_max: maxPrice.trim() || null,
        absolute_min: null,
        allow_marketplace: false,
        baseline_interval_seconds: null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Track a product</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Product URL (optional)"
            placeholder={PLACEHOLDER}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            fullWidth
            autoFocus
            multiline
            maxRows={3}
            helperText="Paste it and the fields below fill in. No page yet? Skip it and set the retailer and item number by hand."
          />

          {previewError ? (
            <Alert severity="warning">
              {previewError} You can still set the retailer and item number by hand.
            </Alert>
          ) : null}

          {preview?.already_tracked ? (
            <Alert severity="info">This product is already on the watchlist.</Alert>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Retailer"
              value={retailer}
              onChange={(event) => setRetailer(event.target.value)}
              fullWidth
              helperText="Each retailer has its own adapter."
            >
              {RETAILERS.map((entry) => (
                <MenuItem key={entry.value} value={entry.value}>
                  {entry.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={selected ? selected.idLabel : 'Item identifier'}
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              fullWidth
              helperText={preview ? 'Read from the URL.' : (selected?.hint ?? 'From the product page.')}
            />
          </Stack>

          <TextField
            label="Name"
            placeholder="30th Celebration Elite Trainer Box"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            helperText="What you want the notification to call it."
          />

          {preview?.suggested_name && preview.suggested_name !== name ? (
            <Box>
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => setName(preview.suggested_name ?? '')}
                label={`Use "${preview.suggested_name}"`}
              />
            </Box>
          ) : null}

          <NotifyListPicker
            value={notifyLists}
            onChange={setNotifyLists}
            options={knownLists ?? []}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="MSRP"
              placeholder="49.99"
              value={msrp}
              onChange={(event) => setMsrp(event.target.value)}
              fullWidth
              helperText="Optional. Sets the sanity floor too."
            />
            <TextField
              label="Max price"
              placeholder="54.99"
              value={maxPrice}
              onChange={(event) => setMaxPrice(event.target.value)}
              fullWidth
              helperText="Optional. Above this, no alert."
            />
          </Stack>

          <TextField
            label="Escalation group"
            placeholder="30th-celebration-etb"
            value={groupKey}
            onChange={(event) => setGroupKey(event.target.value)}
            fullWidth
            helperText="Optional. Products sharing a group speed each other up when one goes live."
          />

          {create.isError ? (
            <Alert severity="error">{(create.error as Error).message}</Alert>
          ) : null}

          {preview ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Detected from the link:
              </Typography>{' '}
              <Chip size="small" variant="outlined" label={`${preview.retailer} ${preview.identifier}`} />
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button size="small" variant="contained" onClick={submit} disabled={!canSubmit}>
          {create.isPending ? 'Adding' : 'Track it'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
